import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription, tap } from 'rxjs';
import { WebSocketController } from '@services/web-socket.controller';
import { ToastService } from '@services/toast.service';
import { WebrtcAudioService } from '@services/webrtc-audio.service';
import { AudioCallDeliveryStatus, AudioCallReliableMeta, AudioCallReliableSendResult, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { ConnectServiceBase } from '@services/connect-service.base';
import { CallSessionService } from '@call/services/call-session.service';
import { AudioCallConnectionDebugService } from '@services/audio-call-connection-debug.service';
import { AudioCallFinalizationService } from '@app/services/audio-call-finalization.service';

const MAX_RECONNECTION_ATTEMPT_COUNT: number = 5;
const RECONNECTION_WAITING_TIME: number = 3500;

@Injectable({
  providedIn: 'root'
})
export class ConnectAudioService extends ConnectServiceBase implements OnDestroy {
  public readonly webrtcAudioService: WebrtcAudioService = inject(WebrtcAudioService);
  public readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly webSocketController: WebSocketController = inject(WebSocketController);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly audioCallConnectionDebugService: AudioCallConnectionDebugService = inject(AudioCallConnectionDebugService);
  private readonly finalization: AudioCallFinalizationService = inject(AudioCallFinalizationService);

  public isVideoSharing: boolean = false;
  public isVideoWatching: boolean = false;
  public customerId: number | null = null;
  public executorId: number | null = null;
  public orderId: number | null = null;
  public delayTime: number = RECONNECTION_WAITING_TIME;
  public triesToReconnect: number = 0;
  public maxReconnectionAttemptCount: number = MAX_RECONNECTION_ATTEMPT_COUNT;
  public mediaElement: HTMLVideoElement | HTMLAudioElement | null = null;
  public withAudio: boolean = true;
  public isAudioCallVideoEnabled: boolean = false;
  public readonly remoteStream$: Observable<MediaStream | null> = this.webrtcAudioService.remoteStream$;
  public readonly remoteVideoActive$: Observable<boolean> = this.webrtcAudioService.remoteVideoActive$;
  public readonly startupFailed$: Subject<void> = new Subject<void>();

  private executorSession: object | null = null;
  private executorInitPromise: Promise<void> | null = null;
  private customerSession: object | null = null;
  private customerInitPromise: Promise<void> | null = null;
  private cameraOperation: (() => boolean) | null = null;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;
  private customerReadySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public customerReadyEvent$: Observable<boolean> = this.customerReadySubject.asObservable();
  private audioCallEventSubscription: Subscription | null = null;
  private readonly processedReliableMessageTtlMs: number = 120000;
  private processedCustomerReadyMessages: Map<string, number> = new Map();
  public stopStreamSubject: Subject<boolean> = new Subject<boolean>();

  public ngOnDestroy(): void {
    this.clearTimer();
    this.audioCallEventSubscription?.unsubscribe();
    this.audioCallEventSubscription = null;
  }

  public get inStream(): boolean {
    return this.isVideoSharing || this.isVideoWatching;
  }

  public get isCameraToggleInProgress(): boolean {
    return this.cameraOperation?.() ?? false;
  }

  public get isScreenSharing(): boolean {
    return this.webrtcAudioService.isScreenSharing;
  }

  public get isVideoCallUsingFrontCamera(): boolean {
    const actualFacing = this.webrtcAudioService.getLocalVideoFacing();
    if (actualFacing) return actualFacing === 'user';
    return this.webrtcAudioService.getVideoCallCameraFacing() === 'user';
  }

  public hasRemoteVideoTrack(): boolean {
    return this.webrtcAudioService.hasRemoteVideoTrack();
  }

  public get remoteVideoActive(): boolean {
    return this.webrtcAudioService.remoteVideoActive;
  }

  public get remoteVideoWasActive(): boolean {
    return this.webrtcAudioService.remoteVideoWasActive;
  }

  public hasLiveRemoteVideoTrack(): boolean {
    return this.webrtcAudioService.hasLiveRemoteVideoTrack();
  }

  public getRemoteStream(): MediaStream | null {
    return this.webrtcAudioService.getRemoteStream();
  }

  public hasLocalVideoTrack(): boolean {
    return this.webrtcAudioService.hasLocalVideoTrack();
  }

  public syncAudioCallVideoEnabledFromActiveCameraTrack(): void {
    if (this.webrtcAudioService.isScreenSharing) return;
    if (!this.webrtcAudioService.hasUsableLocalVideoTrack()) return;

    this.isAudioCallVideoEnabled = true;
  }

  public getLocalPreviewStream(): MediaStream | null {
    return this.webrtcAudioService.getLocalPreviewStream();
  }

  public getLocalVideoPictureInPictureStream(): MediaStream | null {
    return this.webrtcAudioService.getLocalVideoPictureInPictureStream();
  }

  public subscribeToEvents(): void {
    if (this.audioCallEventSubscription) return;
    this.audioCallEventSubscription?.unsubscribe();

    this.audioCallEventSubscription = this.webSocketController.audioCallEvent.subscribe((res: any): void => {
      if (res?.type === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY) {
        this.audioCallConnectionDebugService.track(
          'connect',
          'customer_ready.received',
          {
            actionKey: 'call.audioCall.debugStatus.received',
            eventKey: 'call.audioCall.debugEvents.customerReady',
            tone: 'neutral',
          },
          {detail: res},
        );
        const meta: AudioCallReliableMeta | null = res?.meta ?? null;
        const messageId: string | null = typeof meta?.message_id === 'string' && meta.message_id.length > 0
          ? meta.message_id
          : null;
        if (messageId && this.isProcessedCustomerReadyMessage(messageId)) {
          this.audioCallConnectionDebugService.track(
            'connect',
            'customer_ready.duplicate',
            {
              actionKey: 'call.audioCall.debugStatus.confirmed',
              eventKey: 'call.audioCall.debugEvents.customerReady',
              tone: 'success',
            },
            {
              detail: {messageId, meta},
              updateCurrentStatus: false,
            },
          );
          if (meta) {
            this.webSocketController.emitAudioCallDeliveryReceipt(meta, 'duplicate');
          }
          return;
        }

        let status: AudioCallDeliveryStatus = 'ignored';
        let errorMessage: string | undefined;

        try {
          this.audioCallConnectionDebugService.track(
            'connect',
            'customer_ready.processing',
            {
              actionKey: 'call.audioCall.debugStatus.processing',
              eventKey: 'call.audioCall.debugEvents.customerReady',
              tone: 'neutral',
            },
            {
              detail: {
                to_id: res?.data?.to_id ?? null,
                order_id: res?.data?.order_id ?? null,
              },
            },
          );
          if (res?.data?.to_id && res?.data?.order_id) {
            const currentOrderId: number | null | undefined = this.callSessionService.selectedOrder?.id;
            const currentUserId: number | null | undefined = this.callSessionService.selectedOrder?.myUserId;

            if (!currentOrderId || res.data.order_id !== currentOrderId) {
              status = 'ignored';
            } else if (currentUserId && res.data.to_id !== currentUserId) {
              status = 'ignored';
            } else {
              this.customerReadySubject.next(true);
              status = 'processed';
            }
          } else {
            status = 'ignored';
          }
        } catch (error) {
          status = 'failed';
          errorMessage = error instanceof Error ? error.message : 'Failed to process customer ready event';
        }

        if (messageId && status === 'processed') {
          this.markCustomerReadyMessageProcessed(messageId);
        }
        this.trackCustomerReadyStatus(status, {
          messageId,
          errorMessage,
          order_id: res?.data?.order_id ?? null,
          to_id: res?.data?.to_id ?? null,
        });
        if (meta) {
          this.webSocketController.emitAudioCallDeliveryReceipt(meta, status, errorMessage);
        }
      }
    });
  }

  private cleanupProcessedCustomerReadyMessages(): void {
    const now: number = Date.now();
    this.processedCustomerReadyMessages.forEach((expiresAt: number, messageId: string): void => {
      if (expiresAt > now) return;
      this.processedCustomerReadyMessages.delete(messageId);
    });
  }

  private isProcessedCustomerReadyMessage(messageId: string): boolean {
    this.cleanupProcessedCustomerReadyMessages();
    const expiresAt: number | undefined = this.processedCustomerReadyMessages.get(messageId);
    if (!expiresAt) return false;
    if (expiresAt <= Date.now()) {
      this.processedCustomerReadyMessages.delete(messageId);
      return false;
    }
    return true;
  }

  private markCustomerReadyMessageProcessed(messageId: string): void {
    this.cleanupProcessedCustomerReadyMessages();
    this.processedCustomerReadyMessages.set(messageId, Date.now() + this.processedReliableMessageTtlMs);
  }

  private trackCustomerReadyStatus(
    status: AudioCallDeliveryStatus,
    detail: {
      messageId: string | null;
      errorMessage?: string;
      order_id?: number | null;
      to_id?: number | null;
    },
  ): void {
    if (status === 'processed') {
      this.audioCallConnectionDebugService.track(
        'connect',
        'customer_ready.processed',
        {
          actionKey: 'call.audioCall.debugStatus.ready',
          eventKey: 'call.audioCall.debugEvents.customerReady',
          tone: 'success',
        },
        {detail},
      );
      return;
    }

    if (status === 'ignored') {
      this.audioCallConnectionDebugService.track(
        'connect',
        'customer_ready.ignored',
        {
          actionKey: 'call.audioCall.debugStatus.ignored',
          eventKey: 'call.audioCall.debugEvents.customerReady',
          tone: 'warning',
        },
        {detail},
      );
      return;
    }

    if (status === 'failed') {
      this.audioCallConnectionDebugService.track(
        'connect',
        'customer_ready.failed',
        {
          actionKey: 'call.audioCall.debugStatus.failed',
          eventKey: 'call.audioCall.debugEvents.customerReady',
          tone: 'error',
        },
        {detail},
      );
    }
  }

  private async emitAudioCallExitWithDiagnostics(partnerId: number, orderId: number, context: string): Promise<void> {
    const result: AudioCallReliableSendResult = await this.webSocketController.emitAudioCallExit(partnerId, orderId);
    if (result.delivered) return;

    console.warn(`[ConnectAudioService] audioCallExit is not delivered (${context})`, {
      partnerId,
      orderId,
      result,
    });
  }

  private isRecoverableExecutorInitError(error: unknown): boolean {
    return error instanceof Error
      && error.message.includes('[WebrtcAudioService] Offer was not delivered (init_connection)');
  }

  public async startSharingByExecutor(
    customerId: number,
    orderId: number,
    mediaElement: HTMLVideoElement | HTMLAudioElement
  ): Promise<void> {
    if (this.executorSession) {
      if (this.orderId !== orderId || this.customerId !== customerId) {
        throw new Error('Another audio call is already initializing');
      }
      await this.executorInitPromise;
      return;
    }
    this.audioCallConnectionDebugService.track(
      'connect',
      'executor.start_sharing',
      {
        actionKey: 'call.audioCall.debugStatus.initializingPeer',
        eventKey: 'call.audioCall.debugEvents.peer',
        tone: 'neutral',
      },
      {
        detail: {
          customerId,
          orderId,
        },
      },
    );
    this.customerId = customerId;
    this.orderId = orderId;
    this.mediaElement = mediaElement;
    this.isAudioCallVideoEnabled = false;
    this.withAudio = true;

    this.triesToReconnect = 0;
    const session: object = {};
    this.executorSession = session;
    await this.startExecutorAttempt(session);
  }

  private startExecutorAttempt(session: object): Promise<void> {
    const attempt: Promise<void> = this.initSharing(session).finally((): void => {
      if (this.executorSession === session && this.executorInitPromise === attempt) {
        this.executorInitPromise = null;
      }
    });
    this.executorInitPromise = attempt;
    return attempt;
  }

  private async initSharing(session: object): Promise<void> {
    if (this.executorSession !== session) return;
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    this.isVideoSharing = true;
    try {
      await this.webrtcAudioService.initPeerConnection(
        this.customerId,
        this.orderId,
        this.mediaElement,
        false,
        false,
        (): void => {
          if (this.executorSession === session) this.changeCallbackForExecutor();
        },
        (error: unknown): void => this.errorHandlerCallbackForExecutor(error, session),
      );
    } catch (error) {
      if (this.executorSession !== session) return;
      if (!this.isRecoverableExecutorInitError(error)) {
        throw error;
      }

      this.audioCallConnectionDebugService.track(
        'connect',
        'executor.init_offer_retry_scheduled',
        {
          actionKey: 'call.audioCall.debugStatus.retryConnection',
          eventKey: 'call.audioCall.debugEvents.offer',
          tone: 'warning',
        },
        {
          detail: {
            customerId: this.customerId,
            orderId: this.orderId,
            errorMessage: error.message,
          },
        },
      );

      this.errorHandlerCallbackForExecutor(error, session);
    }
  }

  private changeCallbackForExecutor(): void {
    if (this.webrtcAudioService.isConnected) {
      this.isVideoSharing = true;
      this.triesToReconnect = 0;
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
      }
    }
  }

  private errorHandlerCallbackForExecutor(error: unknown, session: object): void {
    if (!error || this.executorSession !== session || this.connectTimeout
      || this.triesToReconnect > this.maxReconnectionAttemptCount) return;
    console.error('errorHandlerCallbackForExecutor', error);
    this.webrtcAudioService.stopPeerConnection();
    this.customerReadySubject.next(false);
    this.isVideoSharing = false;
    this.triesToReconnect++;
    if (this.triesToReconnect > this.maxReconnectionAttemptCount) {
      this.startupFailed$.next();
      return;
    }
    this.connectTimeout = setTimeout((): void => {
      this.connectTimeout = null;
      if (this.executorSession !== session) return;
      void this.startExecutorAttempt(session).catch((): void => {
        if (this.executorSession !== session) return;
        this.webrtcAudioService.stopPeerConnection();
        this.isVideoSharing = false;
        this.triesToReconnect = this.maxReconnectionAttemptCount + 1;
        this.startupFailed$.next();
      });
    }, this.delayTime);
  }

  public async stopSharingForExecutor(sendToPartner: boolean): Promise<void> {
    const partnerId = this.callSessionService.selectedOrder?.partnerId ?? this.customerId;
    const orderId = this.callSessionService.selectedOrder?.id ?? this.orderId;
    const userId = this.callSessionService.selectedOrder?.myUserId;
    const hadEstablishedMedia = !!orderId && this.callSessionService.hasEstablishedMedia(orderId);
    const attemptId = orderId ? this.callSessionService.getAttempt(orderId, userId) : null;
    this.clearTimer();
    this.stopSharing();
    this.orderId = null;
    this.customerId = null;
    if (this.mediaElement) {
      this.mediaElement.pause();
      this.mediaElement.srcObject = null;
      this.mediaElement = null;
    }

    if (sendToPartner) {
      if (orderId && attemptId) void this.finalization.finish(orderId, hadEstablishedMedia, userId, attemptId);
      else if (orderId) void this.finalization.finish(orderId, hadEstablishedMedia, userId);
      if (!partnerId || !orderId) {
        await this.toastService.error('Partner ID or order ID is not defined!');
        console.error('partnerId or orderId is not defined!');
      } else if (!attemptId) {
        void this.emitAudioCallExitWithDiagnostics(partnerId, orderId, 'stopSharingForExecutor');
      }
    }
  }

  public stopSharing(): void {
    this.executorSession = null;
    this.executorInitPromise = null;
    if (this.connectTimeout) clearTimeout(this.connectTimeout);
    this.connectTimeout = null;
    this.webrtcAudioService.stopPeerConnection();
    this.customerReadySubject.next(false);
    this.isVideoSharing = false;
    this.isAudioCallVideoEnabled = false;
    this.withAudio = true;
  }

  public async startWatchingByCustomer(
    executorId: number,
    orderId: number,
    mediaElement: HTMLVideoElement | HTMLAudioElement
  ): Promise<void> {
    if (this.customerSession) {
      if (this.orderId !== orderId || this.executorId !== executorId) {
        throw new Error('Another audio call is already initializing');
      }
      await this.customerInitPromise;
      return;
    }
    this.executorId = executorId;
    this.orderId = orderId;
    this.mediaElement = mediaElement;
    this.isAudioCallVideoEnabled = false;

    if (this.mediaElement) {
      this.mediaElement.muted = false;
    }
    this.withAudio = true;

    const session: object = {};
    this.customerSession = session;
    const attempt: Promise<void> = this.initWatching(session);
    this.customerInitPromise = attempt;
    try {
      await attempt;
    } catch (error) {
      if (this.customerSession === session) throw error;
    } finally {
      if (this.customerSession === session) this.customerInitPromise = null;
    }
  }

  private async initWatching(session: object): Promise<void> {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
    }
    this.triesToReconnect = 0;

    await this.webrtcAudioService.initPeerConnection(
      this.executorId,
      this.orderId,
      this.mediaElement,
      true,
      false,
      (): void => {
        if (this.customerSession === session) this.changeCallbackForCustomer();
      },
      (error: unknown): void => {
        if (this.customerSession === session) this.errorHandlerCallbackForCustomer(error);
      },
    );

    if (this.customerSession !== session) return;
    this.isVideoWatching = true;
  }

  private changeCallbackForCustomer(): void {
    if (this.webrtcAudioService.isConnected) {
      this.isVideoWatching = true;
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
      }
    }
  }

  private errorHandlerCallbackForCustomer(error: unknown): void {
    console.error('errorHandlerCallbackForCustomer', error);
    if (error) {
      console.error(error);
      let waitingTime: number;
      waitingTime = this.maxReconnectionAttemptCount * this.delayTime;
      this.triesToReconnect = 1;
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout);
      }
      this.connectTimeout = setTimeout(() => {
        console.warn('Attempts to reconnect have ended');
      }, waitingTime + this.delayTime);
    }
  }

  public async stopWatchingByCustomer(sendToPartner: boolean): Promise<void> {
    const partnerId = this.callSessionService.selectedOrder?.partnerId ?? this.executorId;
    const orderId = this.callSessionService.selectedOrder?.id ?? this.orderId;
    const userId = this.callSessionService.selectedOrder?.myUserId;
    const hadEstablishedMedia = !!orderId && this.callSessionService.hasEstablishedMedia(orderId);
    const attemptId = orderId ? this.callSessionService.getAttempt(orderId, userId) : null;
    this.clearTimer();
    this.stopWatching();
    this.orderId = null;
    this.executorId = null;
    if (this.mediaElement) {
      this.mediaElement.pause();
      this.mediaElement.srcObject = null;
      this.mediaElement = null;
    }

    if (sendToPartner) {
      if (orderId && attemptId) void this.finalization.finish(orderId, hadEstablishedMedia, userId, attemptId);
      else if (orderId) void this.finalization.finish(orderId, hadEstablishedMedia, userId);
      if (!partnerId || !orderId) {
        await this.toastService.error('Partner ID or order ID is not defined!');
        console.error('partnerId or orderId is not defined!');
      } else if (!attemptId) {
        void this.emitAudioCallExitWithDiagnostics(partnerId, orderId, 'stopWatchingByCustomer');
      }
    }
  }

  public stopWatching(): void {
    this.customerSession = null;
    this.customerInitPromise = null;
    if (this.connectTimeout) clearTimeout(this.connectTimeout);
    this.connectTimeout = null;
    this.webrtcAudioService.stopPeerConnection();
    this.isVideoWatching = false;
    this.isAudioCallVideoEnabled = false;
    this.withAudio = true;
  }

  public toggleMic(): void {
    this.withAudio = !this.withAudio;
    this.webrtcAudioService.toggleMic(this.withAudio);
  }

  public async toggleInputDevice(): Promise<void> {
    await this.webrtcAudioService.toggleInputDevice();
  }

  public async setAudioInputDevice(deviceId: string): Promise<void> {
    await this.webrtcAudioService.setInputDevice(deviceId);
  }

  public async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
    return await this.webrtcAudioService.listAudioInputDevices();
  }

  public getCurrentAudioInputDeviceId(): string | null {
    return this.webrtcAudioService.getCurrentAudioInputDeviceId();
  }

  public toggleAudioMode(): void {
    this.webrtcAudioService.toggleAudioMode();
  }

  public async toggleCameraForAudioCall(): Promise<void> {
    const isCurrent = this.beginCameraOperation();
    if (!isCurrent) return;

    this.syncAudioCallVideoEnabledFromActiveCameraTrack();
    const wasEnabled: boolean = this.isAudioCallVideoEnabled;

    try {
      if (wasEnabled) {
        if (this.webrtcAudioService.isScreenSharing) {
          // Camera toggle should not stop active screen sharing; it only controls
          // whether we restore the camera once sharing is stopped.
          this.webrtcAudioService.disableCameraRestoreAfterScreenShare();
        } else {
          await this.webrtcAudioService.disableLocalVideo();
        }

        if (!isCurrent()) return;
        this.isAudioCallVideoEnabled = this.webrtcAudioService.hasUsableLocalVideoTrack();
        return;
      }

      if (this.webrtcAudioService.isScreenSharing) {
        await this.webrtcAudioService.stopScreenShare();
        if (!isCurrent()) return;
      }

      await this.webrtcAudioService.enableLocalVideo();
      if (!isCurrent()) return;
      this.isAudioCallVideoEnabled = this.webrtcAudioService.hasUsableLocalVideoTrack();
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to toggle camera for audio call', error);
      this.isAudioCallVideoEnabled = wasEnabled;
      throw error;
    } finally {
      if (this.cameraOperation === isCurrent) this.cameraOperation = null;
    }
  }

  public async switchCameraForVideoCall(): Promise<void> {
    if (!this.isAudioCallVideoEnabled) return;
    const isCurrent = this.beginCameraOperation();
    if (!isCurrent) return;

    try {
      await this.webrtcAudioService.toggleVideoCallCameraFacing();
    } catch (error) {
      if (!isCurrent()) return;
      throw error;
    } finally {
      if (isCurrent()) this.isAudioCallVideoEnabled = this.webrtcAudioService.hasLocalVideoTrack();
      if (this.cameraOperation === isCurrent) this.cameraOperation = null;
    }
  }

  public async restoreCameraForAudioCallIfNeeded(): Promise<void> {
    if (!this.isAudioCallVideoEnabled) return;
    if (this.webrtcAudioService.isScreenSharing) return;
    if (this.webrtcAudioService.hasUsableLocalVideoTrack()) return;

    const isCurrent = this.beginCameraOperation();
    if (!isCurrent) return;
    try {
      await this.webrtcAudioService.restoreLocalVideoTrack();
    } catch (error) {
      if (!isCurrent()) return;
      throw error;
    } finally {
      if (isCurrent()) this.isAudioCallVideoEnabled = this.webrtcAudioService.hasUsableLocalVideoTrack();
      if (this.cameraOperation === isCurrent) this.cameraOperation = null;
    }
  }

  public async toggleScreenShare(): Promise<void> {
    if (this.webrtcAudioService.isScreenSharing) {
      await this.webrtcAudioService.stopScreenShare();
      return;
    }
    await this.webrtcAudioService.startScreenShare();
  }

  public async startTimer(): Promise<void> {
    if (this.timerSubscription) return;

    this.streamTimeSubject.next('00:00');

    this.timerSubscription = interval(1000).pipe(
      tap((): void => {
        if (this.webrtcAudioService.isConnected) {
          if (!this.webrtcAudioService.streamStartTime) return;

          this.streamTime++;

          const now: number = Date.now();
          const elapsed: number = now - this.webrtcAudioService.streamStartTime;

          const seconds: number = Math.floor((elapsed % (1000 * 60)) / 1000);

          const hours: number = Math.floor(elapsed / (1000 * 60 * 60));
          const minutes: number = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));

          const formattedTime: string = this.formatTime(hours, minutes, seconds);
          this.streamTimeSubject.next(formattedTime);

          const totalSeconds: number = Math.floor(elapsed / 1000);
          this.totalSecondsSubject.next(totalSeconds * 1000);
        }
      })
    ).subscribe();
  }

  private beginCameraOperation(): (() => boolean) | null {
    if (this.isCameraToggleInProgress) return null;
    const identity = this.callSessionService.mediaSessionIdentity;
    const peer = this.webrtcAudioService.peerConnection;
    if (!identity || !peer || peer.connectionState === 'closed' || peer.signalingState === 'closed') return null;

    // A replacement call/peer may start immediately, without waiting for old camera work.
    const isCurrent = (): boolean => this.cameraOperation === isCurrent
      && this.callSessionService.mediaSessionIdentity === identity
      && this.webrtcAudioService.peerConnection === peer
      && peer.connectionState !== 'closed' && peer.signalingState !== 'closed';
    this.cameraOperation = isCurrent;
    return isCurrent;
  }
}
